import axios from "axios";
import React from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { BASE_API_URL } from "store/Me";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SiteResponse, SiteSystemType } from "api/ApiClient";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { ArrowLeft, ChevronDown, Pencil, Trash2 } from "lucide-react";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import SiteProxyEditModal from "./SiteProxyEditModal";
import { SyncPreviewModal } from "../Admin/SyncPreviewModal";
import { ImportModal } from "../Admin/ImportModal";
import SiteGateways from "./SiteGateways";
import SiteAccessControlGroups from "./SiteAccessControlGroups";
import SiteKeyRequests from "./SiteKeyRequests";
import SiteLocks from "./SiteLocks";
import SiteGuests from "./SiteGuests";
import SiteAccessSchedulesTab from "./SiteAccessSchedulesTab";
import SiteSyncRecords from "./SiteSyncRecords";
import SiteKeys from "./SiteKeys";
import SiteDetailItem from "./SiteDetailItem";
import GuestDetails from "../GuestDetails";
import OnboardGuest from "../OnboardGuest";
import SiteEditModal from "./SiteEditModal";
import { DEFAULT_FOB_TYPE, type DefaultFobType, fobTypeLabel } from "./fobTypes";
import { cadenceLabel } from "./dbDownloadCadence";
import SiteStatusModal, { type SiteStatus } from "./SiteStatusModal";
import { useSiteSyncWatcher } from "./useSiteSyncWatcher";
import { useIsSuperUser } from "shared/api/useIsSuperUser";
import FobSyncConfirmDialog from "components/shared/FobSyncConfirmDialog";
import { requestErrorMessage } from "./siteKeysApi";
import { statusBadgeVariant } from "shared/utils/statusChipColor";

// SiteSyncOperationFlag values for POST api/Sites/{id}/sync?flags=
const SYNC_FLAG_LOCKS = 1;
const SYNC_FLAG_UNITS = 2;
const SYNC_FLAG_PHYSICAL_KEYS = 8;

// Extended type until NSwag regeneration includes these fields
interface FullSiteResponse extends SiteResponse {
  propertyExternalId?: string;
  accessCode?: string;
  proxyVersion?: number;
  dormaRemoveKeyEnabled?: boolean;
  schlageSharePhysicalKeysEnabled?: boolean;
  defaultFobType?: DefaultFobType;
  dbDownloadIntervalMinutes?: number | null;
  dbDownloadAnchorMinuteOfDay?: number | null;
  hasPendingChanges?: boolean;
  status?: SiteStatus;
}

async function getSite(siteId: string): Promise<FullSiteResponse> {
  const result = await axios.get<FullSiteResponse>(
    `${BASE_API_URL}/api/Sites/${siteId}`,
  );
  return result.data;
}

function useGetSite(siteId: string) {
  return useQuery({
    queryKey: ["sites", "details", siteId],
    queryFn: () => getSite(siteId),
  });
}

interface SiteSummary {
  lockCount: number;
  lockOnlineCount: number;
  lockOfflineCount: number;
  gatewayCount: number;
  gatewayOnlineCount: number;
  gatewayOfflineCount: number;
  accessScheduleCount: number;
  lastLocksSyncAt?: string;
  lastUnitsSyncAt?: string;
}

async function getSiteSummary(siteId: string): Promise<SiteSummary> {
  const result = await axios.get<SiteSummary>(
    `${BASE_API_URL}/api/Sites/${siteId}/summary`,
  );
  return result.data;
}

function useGetSiteSummary(siteId: string) {
  return useQuery({
    queryKey: ["sites", "summary", siteId],
    queryFn: () => getSiteSummary(siteId),
  });
}

function getSiteNavLinks(systemType: SiteSystemType | undefined) {
  const links: { to: string; label: string }[] = [];

  if (systemType === SiteSystemType.SchlageEngage) {
    links.push({ to: "gateways", label: "Gateways" });
  }

  links.push({ to: "locks", label: "Locks" });

  if (
    systemType === (SiteSystemType.Brivo as string) ||
    systemType === (SiteSystemType.Openpath as string)
  ) {
    links.push({ to: "groups", label: "Groups" });
  }

  links.push(
    { to: "guests", label: "Guests" },
    { to: "access-schedules", label: "Access Schedules" },
    { to: "sync-records", label: "Sync Records" },
    { to: "key-requests", label: "Key Requests" },
  );

  if (systemType === SiteSystemType.SchlageEngage) {
    // One entry for the Fobs list, the key Audit log and the fob detail (all under keys/).
    links.push({ to: "keys", label: "Keys" });
  }

  return links;
}

function SiteNavigation({
  siteId,
  systemType,
}: {
  siteId: string;
  systemType: SiteSystemType | undefined;
}) {
  const links = getSiteNavLinks(systemType);
  const basePath = `/admin/sites/${siteId}`;
  const location = useLocation();
  const navigate = useNavigate();

  const activeTo =
    links.find(({ to }) => {
      const path = `${basePath}/${to}`;
      return (
        location.pathname === path || location.pathname.startsWith(`${path}/`)
      );
    })?.to ?? links[0]?.to;

  return (
    <Tabs
      value={activeTo}
      onValueChange={(value) => navigate(`${basePath}/${value}`)}
    >
      <TabsList className="justify-start w-full overflow-x-auto overflow-y-hidden">
        {links.map(({ to, label }) => (
          <TabsTrigger
            key={to}
            value={to}
            onClick={() => navigate(`${basePath}/${to}`)}
            className="shrink-0 data-[state=active]:bg-slate-200!"
          >
            {label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

function SiteInfoPanel({
  site,
  siteId,
  onEditClick,
  onDeleteClick,
}: {
  site: FullSiteResponse;
  siteId: string;
  onEditClick: () => void;
  onDeleteClick: () => void;
}) {
  const queryClient = useQueryClient();
  const st = site.account?.systemType as string | undefined;
  const isSchlage = st === SiteSystemType.SchlageEngage;
  const isDorma = st === SiteSystemType.DormaKaba;
  const isProxy = [
    SiteSystemType.Assa as string,
    SiteSystemType.SaltoSpace as string,
    SiteSystemType.DormaKaba as string,
  ].includes(st ?? "");

  // Site sync split-button state (Locks=1, Units=2; both pre-checked by default)
  // Units is disabled when the site has multiple properties (propertyExternalId is semicolon-joined)
  // Fobs=8 is offered only to super users on Schlage sites sharing physical keys, and never
  // pre-checked: it syncs every fob to the sister Brivo sites, which is heavier than Locks or Units.
  const hasMultipleProperties = !!site.propertyExternalId?.includes(";");
  const canSyncFobs = useIsSuperUser();
  const sharesPhysicalKeys =
    canSyncFobs && isSchlage && !!site.schlageSharePhysicalKeysEnabled;
  const SITE_SYNC_FLAGS = [
    {
      id: SYNC_FLAG_LOCKS,
      name: "Locks",
      short: "Lck",
      description: "Sync lock inventory from access control system",
    },
    {
      id: SYNC_FLAG_UNITS,
      name: "Units",
      short: "Unt",
      description: "Sync unit and ACG data",
    },
    ...(sharesPhysicalKeys
      ? [
          {
            id: SYNC_FLAG_PHYSICAL_KEYS,
            name: "Fobs",
            short: "Fob",
            description: "Sync physical keys to sister Brivo sites",
          },
        ]
      : []),
  ];
  const [selectedSyncFlags, setSyncFlags] = React.useState<number[]>(() =>
    hasMultipleProperties ? [SYNC_FLAG_LOCKS] : [SYNC_FLAG_LOCKS, SYNC_FLAG_UNITS],
  );
  // Only options still on offer (e.g. Fobs drops out if sharing is turned off).
  const syncFlags = selectedSyncFlags.filter((flag) =>
    SITE_SYNC_FLAGS.some((option) => option.id === flag),
  );
  // The preview covers locks and units; it has nothing to show for fobs (it notes that they'll
  // sync too, and "Run Sync Now" still sends every selected flag).
  const previewSyncFlags = syncFlags.filter(
    (flag) => flag !== SYNC_FLAG_PHYSICAL_KEYS,
  );
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);

  // The sync endpoint only queues the work; this follows the queued sync to completion and then
  // refreshes the site's views (keys, sync records, details, summary).
  const { watchNewSync } = useSiteSyncWatcher(siteId);

  const {
    mutate: runSync,
    isPending: isSyncing,
    isError: isSyncError,
    error: syncError,
  } = useMutation({
    mutationFn: () => {
      const flags = syncFlags.reduce((acc, f) => acc | f, 0) || 1;
      return axios.post(
        `${BASE_API_URL}/api/Sites/${siteId}/sync?flags=${flags}`,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites", "details", siteId] });
      queryClient.invalidateQueries({ queryKey: ["sites", "summary", siteId] });
      watchNewSync();
    },
  });

  // Every sync that includes Fobs is confirmed first; other syncs run straight away.
  const [confirmFobSyncOpen, setConfirmFobSyncOpen] = React.useState(false);
  const requestSync = () => {
    if (syncFlags.includes(SYNC_FLAG_PHYSICAL_KEYS)) {
      setConfirmFobSyncOpen(true);
    } else {
      runSync();
    }
  };

  const toggleSyncFlag = (flag: number) => {
    setSyncFlags((prev) =>
      prev.includes(flag) ? prev.filter((f) => f !== flag) : [...prev, flag],
    );
  };

  const selectedShort = SITE_SYNC_FLAGS.filter((f) =>
    syncFlags.includes(f.id),
  ).map((f) => f.short);
  const syncLabel = isSyncing
    ? "Syncing..."
    : selectedShort.length === SITE_SYNC_FLAGS.length
      ? "Sync All"
      : selectedShort.length === 0
        ? "Sync"
        : `Sync (${selectedShort.join(", ")})`;

  return (
    <section className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
      <header className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-200">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            Site details
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Configuration and sync controls
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={`Edit ${site.name}`}
                className="text-primary hover:text-primary"
                onClick={onEditClick}
                size="icon-sm"
                type="button"
                variant="ghost"
              >
                <Pencil />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit site</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                aria-label={`Delete ${site.name}`}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={onDeleteClick}
                size="icon-sm"
                type="button"
                variant="ghost"
              >
                <Trash2 />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete site</TooltipContent>
          </Tooltip>
        </div>
      </header>
      <dl className="divide-y divide-slate-100">
        <SiteDetailItem label="System Site ID">
          {site.systemSiteId ?? "—"}
        </SiteDetailItem>
        <SiteDetailItem label="System Type">
          <Badge variant="outline">
            {site.account?.systemType ?? "Unknown"}
          </Badge>
        </SiteDetailItem>
        <SiteDetailItem label="Status">
          <Badge asChild variant={statusBadgeVariant(site.status)}>
            <button
              aria-label="Change site status"
              className="cursor-pointer"
              onClick={() => setStatusModalOpen(true)}
              type="button"
            >
              {site.status ?? "Staging"}
            </button>
          </Badge>
        </SiteDetailItem>
        <SiteDetailItem label="Time Zone">
          {site.timeZone ?? "—"}
        </SiteDetailItem>
        {site.propertyExternalId && (
          <SiteDetailItem label="Property External ID">
            {site.propertyExternalId}
          </SiteDetailItem>
        )}
        {isSchlage && site.accessCode && (
          <SiteDetailItem label="Access Code">{site.accessCode}</SiteDetailItem>
        )}
        {isSchlage && site.schlageSharePhysicalKeysEnabled !== undefined && (
          <SiteDetailItem label="Share Physical Keys">
            {site.schlageSharePhysicalKeysEnabled ? "Enabled" : "Disabled"}
          </SiteDetailItem>
        )}
        {isSchlage && (
          <SiteDetailItem label="Default Fob Type">
            {fobTypeLabel(site.defaultFobType ?? DEFAULT_FOB_TYPE)}
          </SiteDetailItem>
        )}
        {isSchlage && (
          <SiteDetailItem label="DB Download Cadence">
            {cadenceLabel(site.dbDownloadIntervalMinutes, site.dbDownloadAnchorMinuteOfDay, site.timeZone)}
          </SiteDetailItem>
        )}
        {isDorma && site.dormaRemoveKeyEnabled !== undefined && (
          <SiteDetailItem label="Remove Key on Deactivation">
            {site.dormaRemoveKeyEnabled ? "Enabled" : "Disabled"}
          </SiteDetailItem>
        )}
        {isProxy &&
          site.proxyVersion !== undefined &&
          site.proxyVersion !== null && (
            <SiteDetailItem label="Proxy Version">
              {site.proxyVersion}
            </SiteDetailItem>
          )}
      </dl>
      <SiteStatusModal
        show={statusModalOpen}
        onHide={() => setStatusModalOpen(false)}
        siteId={siteId}
        currentStatus={site.status}
      />
      <div className="grid grid-cols-2 gap-2 p-4 border-t border-slate-200">
        <Button
          onClick={() => setImportOpen(true)}
          size="sm"
          type="button"
          variant="outline"
        >
          Import CSV
        </Button>
        <Button
          disabled={isSyncing || previewSyncFlags.length === 0}
          onClick={() => setPreviewOpen(true)}
          size="sm"
          type="button"
          variant="outline"
        >
          Preview Sync
        </Button>
        <DropdownMenu>
          <div className="flex col-span-2">
            <Button
              onClick={requestSync}
              disabled={isSyncing || syncFlags.length === 0}
              className="flex-1 px-0 rounded-r-none"
              size="sm"
              type="button"
            >
              {syncLabel}
            </Button>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label="Choose sync options"
                className="border-l rounded-l-none shrink-0 border-l-primary-foreground/30"
                disabled={isSyncing}
                size="icon-sm"
                type="button"
              >
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
          </div>
          <DropdownMenuContent align="end">
            {SITE_SYNC_FLAGS.map((item) => (
              <DropdownMenuCheckboxItem
                checked={syncFlags.includes(item.id)}
                disabled={hasMultipleProperties && item.id === SYNC_FLAG_UNITS}
                key={item.id}
                onSelect={(event) => {
                  event.preventDefault();
                  toggleSyncFlag(item.id);
                }}
              >
                <span className="grid gap-0.5">
                  <span>{item.name}</span>
                  <span className="text-[11px] text-muted-foreground">
                    {item.description}
                  </span>
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {isSyncError && (
        // e.g. the API's 401 message when someone who isn't a super user requests Fobs.
        <Alert className="m-4" variant="destructive">
          Sync failed: {requestErrorMessage(syncError, "the sync couldn't be queued.")}
        </Alert>
      )}
      {hasMultipleProperties && (
        <Alert className="m-4" variant="warning">
          Unit sync is unavailable because this site is associated with multiple
          properties.
        </Alert>
      )}
      {previewOpen && (
        <SyncPreviewModal
          siteId={siteId}
          siteName={site.name ?? ""}
          syncFlags={previewSyncFlags}
          unpreviewedNote={
            syncFlags.includes(SYNC_FLAG_PHYSICAL_KEYS)
              ? "Fobs will also sync to sister sites (no preview available)."
              : undefined
          }
          onClose={() => setPreviewOpen(false)}
          onRunSync={() => {
            requestSync();
            setPreviewOpen(false);
          }}
        />
      )}
      <FobSyncConfirmDialog
        open={confirmFobSyncOpen}
        siteName={site.name ?? ""}
        onClose={() => setConfirmFobSyncOpen(false)}
        onConfirm={() => {
          setConfirmFobSyncOpen(false);
          runSync();
        }}
      />
      {importOpen && (
        <ImportModal
          siteId={siteId}
          siteName={site.name ?? ""}
          propertyExternalId={site.propertyExternalId ?? undefined}
          onClose={() => setImportOpen(false)}
        />
      )}
    </section>
  );
}

type ProxyStatus = "Unknown" | "Online" | "Offline" | "Error";

interface SiteProxyInfo {
  id: string;
  name: string;
  siteId: string;
  status: ProxyStatus;
  version: number;
  softwareVersion?: string;
  lastSeenTimestamp?: string;
  username?: string;
  sharedKeyName?: string;
}

async function getSiteProxies(siteId: string): Promise<SiteProxyInfo[]> {
  const params = new URLSearchParams();
  params.append("siteId", siteId);
  const res = await axios.get<SiteProxyInfo[]>(
    `${BASE_API_URL}/api/proxies?${params.toString()}`,
  );
  return res.data;
}

function useGetSiteProxies(siteId: string) {
  return useQuery({
    queryKey: ["sites", "proxies", siteId],
    queryFn: () => getSiteProxies(siteId),
  });
}

function proxyStatusDotClass(status: ProxyStatus): string {
  if (status === "Online") return "bg-green-500";
  if (status === "Offline") return "bg-slate-400";
  if (status === "Error") return "bg-red-500";
  return "bg-slate-400";
}

function SiteProxyPanel({ siteId }: { siteId: string }) {
  const queryClient = useQueryClient();
  const { data: proxies, isLoading } = useGetSiteProxies(siteId);
  const [editProxy, setEditProxy] = React.useState<SiteProxyInfo | null>(null);

  if (isLoading) return null;
  if (!proxies || proxies.length === 0) return null;

  return (
    <>
      <section className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
        <header className="px-4 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Proxies</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            {proxies.length} {proxies.length === 1 ? "proxy" : "proxies"}{" "}
            configured
          </p>
        </header>
        <div className="divide-y divide-slate-100">
          {proxies.map((proxy) => (
            <article key={proxy.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold truncate text-slate-900">
                    {proxy.name}
                  </h3>
                  <span className="inline-flex items-center gap-2 mt-1 text-xs font-medium text-slate-600">
                    <span
                      aria-hidden="true"
                      className={`h-1.5 w-1.5 shrink-0 rounded-full ${proxyStatusDotClass(proxy.status)}`}
                    />
                    {proxy.status}
                  </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to={`/admin/locks/proxies/${proxy.id}`}
                        className="text-xs font-semibold text-blue-700 hover:underline"
                      >
                        View
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>Open proxy detail page</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        aria-label={`Edit ${proxy.name} proxy credentials`}
                        className="text-primary hover:text-primary"
                        onClick={() => setEditProxy(proxy)}
                        size="icon-sm"
                        type="button"
                        variant="ghost"
                      >
                        <Pencil />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Edit proxy credentials</TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <dl className="grid grid-cols-2 mt-4 text-sm gap-x-4 gap-y-3">
                <div>
                  <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                    Version
                  </dt>
                  <dd className="mt-1 font-medium text-slate-800">
                    {proxy.version}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                    Last Seen
                  </dt>
                  <dd className="mt-1 font-medium wrap-break-word text-slate-800">
                    {proxy.lastSeenTimestamp
                      ? new Date(proxy.lastSeenTimestamp).toLocaleString()
                      : "Never"}
                  </dd>
                </div>
                {proxy.softwareVersion && (
                  <div className="col-span-2">
                    <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                      Software
                    </dt>
                    <dd className="mt-1 font-mono text-xs break-all text-slate-700">
                      {proxy.softwareVersion}
                    </dd>
                  </div>
                )}
                {proxy.username && (
                  <div className="col-span-2">
                    <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                      Username
                    </dt>
                    <dd className="mt-1 font-medium break-all text-slate-800">
                      {proxy.username}
                    </dd>
                  </div>
                )}
              </dl>
            </article>
          ))}
        </div>
      </section>

      {editProxy && (
        <SiteProxyEditModal
          open={!!editProxy}
          proxyId={editProxy.id}
          proxyName={editProxy.name}
          currentUsername={editProxy.username}
          currentSharedKeyName={editProxy.sharedKeyName}
          onClose={(saved) => {
            setEditProxy(null);
            if (saved)
              queryClient.invalidateQueries({
                queryKey: ["sites", "proxies", siteId],
              });
          }}
        />
      )}
    </>
  );
}

function SiteSummaryStrip({ summary }: { summary: SiteSummary | undefined }) {
  if (!summary) return null;

  const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "Never");

  return (
    <section
      aria-label="Site summary at a glance"
      className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200"
    >
      <dl className="flex flex-wrap divide-x divide-slate-100">
        <div className="px-3 py-2 min-w-32">
          <dt className="text-[10px] font-semibold tracking-wide uppercase text-slate-500">
            Locks
          </dt>
          <dd className="flex flex-wrap text-xs gap-x-1.5 gap-y-0.5">
            <span className="font-medium text-slate-800">
              {summary.lockCount} total
            </span>
            <span className="text-green-700">
              {summary.lockOnlineCount} online
            </span>
            <span className="text-slate-500">
              {summary.lockOfflineCount} offline
            </span>
          </dd>
        </div>
        {summary.gatewayCount > 0 && (
          <div className="px-3 py-2 min-w-32">
            <dt className="text-[10px] font-semibold tracking-wide uppercase text-slate-500">
              Gateways
            </dt>
            <dd className="flex flex-wrap text-xs gap-x-1.5 gap-y-0.5">
              <span className="font-medium text-slate-800">
                {summary.gatewayCount} total
              </span>
              <span className="text-green-700">
                {summary.gatewayOnlineCount} online
              </span>
              <span className="text-slate-500">
                {summary.gatewayOfflineCount} offline
              </span>
            </dd>
          </div>
        )}
        <div className="px-3 py-2 min-w-24">
          <dt className="text-[10px] font-semibold tracking-wide uppercase text-slate-500">
            Schedules
          </dt>
          <dd className="text-xs font-medium text-slate-800">
            {summary.accessScheduleCount}
          </dd>
        </div>
        <div className="px-3 py-2 min-w-40">
          <dt className="text-[10px] font-semibold tracking-wide uppercase text-slate-500">
            Last Locks Sync
          </dt>
          <dd className="text-xs font-medium wrap-break-word text-slate-800">
            {fmt(summary.lastLocksSyncAt)}
          </dd>
        </div>
        <div className="px-3 py-2 min-w-40">
          <dt className="text-[10px] font-semibold tracking-wide uppercase text-slate-500">
            Last Units Sync
          </dt>
          <dd className="text-xs font-medium wrap-break-word text-slate-800">
            {fmt(summary.lastUnitsSyncAt)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

export default function AdminSite() {
  const navigate = useNavigate();
  const params = useParams<{ siteId: string }>();
  const siteId = params.siteId!;
  const { data, isError } = useGetSite(siteId);
  const { data: summary } = useGetSiteSummary(siteId);
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);

  const { mutate: deleteSite, isPending: isDeleting } = useMutation({
    mutationFn: () => axios.delete(`${BASE_API_URL}/api/Sites/${siteId}`),
    onSuccess: () => {
      setConfirmOpen(false);
      navigate("/admin/sites", { replace: true });
      queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "An error occurred deleting the site.";
      setDeleteError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });

  if (isError) {
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <Link
          to="/admin/sites"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Sites
        </Link>
        <Alert variant="destructive">
          Site not found or you do not have access.
        </Alert>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
        <Link
          to="/admin/sites"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Sites
        </Link>
        <div className="flex items-center justify-center text-sm bg-white border rounded-lg shadow-sm min-h-80 border-slate-200 text-slate-500">
          Loading site...
        </div>
      </main>
    );
  }

  const systemType = data.account?.systemType;
  const defaultTab =
    systemType === SiteSystemType.SchlageEngage ? "gateways" : "guests";
  // A Schlage site sharing its fobs with sister (Brivo) sites.
  const sharesPhysicalKeys =
    systemType === SiteSystemType.SchlageEngage &&
    !!data.schlageSharePhysicalKeysEnabled;

  const checksumMismatch = !!data.hasPendingChanges;

  return (
    <main className="py-6 mx-auto space-y-6 max-w-screen-2xl">
      <header className="pb-5 border-b border-slate-200">
        <Link
          to="/admin/sites"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-700"
        >
          <ArrowLeft className="size-4" />
          Back to Sites
        </Link>
      </header>

      {checksumMismatch && (
        <Alert variant="warning">
          This site has pending changes. The current configuration does not
          match what is synced in Connect. Run a sync to bring it up to date.
        </Alert>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(19rem,24rem)_minmax(0,1fr)]">
        <aside className="space-y-4 xl:sticky xl:top-6 xl:self-start">
          <div className="px-1">
            <h1 className="text-2xl font-semibold text-slate-900">
              {data.name ?? "Site"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {data.account?.name ?? "No account assigned"}
            </p>
          </div>
          <SiteInfoPanel
            // Fresh sync selection per site, so an opt-in to Fobs never carries over.
            key={siteId}
            site={data}
            siteId={siteId}
            onEditClick={() => setEditOpen(true)}
            onDeleteClick={() => {
              setDeleteError(null);
              setConfirmOpen(true);
            }}
          />
          <SiteProxyPanel siteId={siteId} />
        </aside>

        <section className="min-w-0">
          <SiteSummaryStrip summary={summary} />
          <div className="mt-4">
            <SiteNavigation siteId={siteId} systemType={systemType} />
          </div>
          <div className="pt-6">
            <Routes>
              <Route
                path="gateways"
                element={<SiteGateways siteId={siteId} />}
              />
              <Route path="locks" element={<SiteLocks siteId={siteId} />} />
              <Route path="guests" element={<SiteGuests siteId={siteId} />} />
              <Route path="guests/:guestId" element={<GuestDetails />} />
              <Route path="onboardguest" element={<OnboardGuest />} />
              <Route
                path="access-schedules"
                element={<SiteAccessSchedulesTab siteId={siteId} />}
              />
              <Route
                path="sync-records"
                element={<SiteSyncRecords siteId={siteId} />}
              />
              <Route
                path="key-requests"
                element={<SiteKeyRequests siteId={siteId} />}
              />
              <Route
                path="groups"
                element={<SiteAccessControlGroups siteId={siteId} />}
              />
              {/* Old bookmarks: the Key Audit tab is now the Keys tab's Audit log. */}
              <Route
                path="keys-audit"
                element={
                  <Navigate to={`/admin/sites/${siteId}/keys/audit`} replace />
                }
              />
              <Route
                path="keys/*"
                element={
                  <SiteKeys
                    siteId={siteId}
                    externalSyncEnabled={sharesPhysicalKeys}
                  />
                }
              />
              <Route index element={<Navigate to={defaultTab} replace />} />
            </Routes>
          </div>
        </section>
      </div>

      <Dialog
        open={confirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setConfirmOpen(false);
          }
        }}
      >
        <DialogContent showCloseButton={false} className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Delete Site</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{data.name}</strong>? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && <Alert variant="destructive">{deleteError}</Alert>}
          <DialogFooter>
            <Button
              disabled={isDeleting}
              onClick={() => setConfirmOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isDeleting}
              onClick={() => deleteSite()}
              type="button"
              variant="destructive"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SiteEditModal
        open={editOpen}
        siteId={siteId}
        initialValues={{
          name: data.name ?? "",
          propertyExternalId: data.propertyExternalId ?? "",
          timeZone: data.timeZone ?? "",
          systemSiteId: data.systemSiteId ?? undefined,
          accessCode: data.accessCode,
          dormaRemoveKeyEnabled: data.dormaRemoveKeyEnabled,
          schlageSharePhysicalKeysEnabled: data.schlageSharePhysicalKeysEnabled,
          defaultFobType: data.defaultFobType,
          dbDownloadIntervalMinutes: data.dbDownloadIntervalMinutes,
          dbDownloadAnchorMinuteOfDay: data.dbDownloadAnchorMinuteOfDay,
        }}
        systemType={data.account?.systemType as string | undefined}
        onClose={(saved) => {
          setEditOpen(false);
          if (saved)
            queryClient.invalidateQueries({
              queryKey: ["sites", "details", siteId],
            });
        }}
      />
    </main>
  );
}
